import React, { useState, useMemo } from 'react';
import { Calendar, CheckCircle2, Circle, Clock, Edit2, Plus, Trash2, ChevronDown, ChevronRight, Target, CreditCard, Banknote, Wallet, Paperclip, Upload, Loader2, AlertTriangle } from 'lucide-react';
import { format, isSameMonth, parseISO, addMonths, endOfMonth, addDays, addWeeks, differenceInCalendarDays } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { PaymentFile, PlannedPayment, RecurringExpense, Debt, Goal, IncomeStream, Transaction, DismissedOccurrence } from '../types';
import { doc, deleteDoc, updateDoc, addDoc, collection, setDoc, serverTimestamp, increment } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../lib/firebase';

interface PlanningScheduleDashboardProps {
  plannedPayments: PlannedPayment[];
  recurringExpenses: RecurringExpense[];
  debts: Debt[];
  goals: Goal[];
  incomes: IncomeStream[];
  transactions: Transaction[];
  paymentFiles: PaymentFile[];
  dismissedOccurrences: DismissedOccurrence[];
  householdId: string;
  /** Map of plannedPayment.id -> cumulative paid amount across all linked transactions */
  commitmentPaidMap?: Map<string, number>;
  onAddSchedule: () => void;
  onEditPayment: (payment: PlannedPayment) => void;
  onMarkAsPaid: (payment: VirtualPayment) => void;
}

/** Deterministic doc ID for a dismissed virtual occurrence -- lets us
 *  create/delete by direct reference instead of a query. */
const dismissedOccurrenceDocId = (sourceId: string, dateKey: string) => `${sourceId}_${dateKey}`;

interface MissedItem {
  key: string;
  commitmentName: string;
  description: string;
  date: Date;
  amount: number;
  alreadyPaid: number;
  isVirtual: boolean;
  /** Only set for custom plannedPayments — needed by handleManualComplete */
  plannedPaymentId?: string;
  /** Only set for virtual (debt/expense) items — needed by
   *  handleDismissVirtualOccurrence/handleRevertVirtualDismissal */
  virtualPayment?: VirtualPayment;
}

interface MatchCandidate {
  /** Stable per-render id, not a Firestore id -- used as the <select> value. */
  key: string;
  label: string;
  date: Date;
  amount: number;
  kind: 'custom' | 'virtual' | 'debt';
  /** Set when kind === 'custom' (a planned_payments doc id). */
  plannedPaymentId?: string;
  /** Set when kind === 'virtual' (the recurring expense's own doc id) or
   *  kind === 'debt' (the debt's own doc id). */
  virtualSourceId?: string;
}

interface SuggestedMatch {
  transactionId: string;
  transactionDescription: string;
  transactionDate: Date;
  transactionAmount: number;
  /** Closest first; candidates[0] is what Approve links if nothing's
   *  been picked in the Adjust dropdown. */
  candidates: MatchCandidate[];
}

export interface VirtualPayment {
  id: string;
  commitmentName: string;
  description: string;
  date: Date;
  amount: number;
  status: 'pending' | 'paid';
  type: 'custom' | 'income' | 'expense' | 'debt' | 'goal';
  sourceId?: string;
  isVirtual: boolean;
  /** True when a real (lower-than-projected) payment was manually
   *  accepted as closing this occurrence — see dismissed_occurrences. */
  manuallyCompleted?: boolean;
}

type VirtualPaymentType = VirtualPayment['type'];

// How far back the Missed Payments card looks for unpaid debt/recurring-
// expense occurrences. Deliberately a fixed calendar date, not a rolling
// "N months back from today" — this household only started tracking
// debts/expenses as real transactions in the app starting August 2026;
// generating occurrences (and flagging them "missed") for the untracked
// months before that would just be false-positive noise, not real misses.
const MISSED_PAYMENTS_TRACKING_START = new Date(2026, 7, 1); // Aug 1, 2026

// Category grouping for the month-by-month breakdown, in display order.
const CATEGORY_ORDER: VirtualPaymentType[] = ['income', 'expense', 'debt', 'custom', 'goal'];
const CATEGORY_LABELS: Record<VirtualPaymentType, string> = {
  income: 'Income',
  expense: 'Fixed Expenses',
  debt: 'Debt Payments',
  custom: 'Custom Commitments',
  goal: 'Goals',
};
const generateOccurrences = (
  item: { id?: string; amount: number; schedule: any; name?: string; category?: string; source?: string },
  type: 'income' | 'expense' | 'debt' | 'goal',
  startDate: Date,
  endDate: Date
): { date: Date; amount: number }[] => {
  const occurrences: { date: Date; amount: number }[] = [];
  const schedule = item.schedule;
  if (!schedule) return occurrences;

  const current = new Date(startDate);
  
  while (current <= endDate) {
    const year = current.getFullYear();
    const month = current.getMonth();
    
    if (schedule.frequency === 'monthly' || schedule.frequency === 'semi-monthly') {
      if (schedule.date1) {
        const d = new Date(year, month, schedule.date1, 12, 0, 0);
        if (d >= startDate && d <= endDate) occurrences.push({ date: d, amount: item.amount });
      }
      if (schedule.frequency === 'semi-monthly' && schedule.date2) {
        const d = new Date(year, month, schedule.date2, 12, 0, 0);
        if (d >= startDate && d <= endDate) occurrences.push({ date: d, amount: item.amount });
      }
      current.setMonth(current.getMonth() + 1);
    } else {
      break; // handled below
    }
  }

  if (schedule.frequency === 'bi-weekly' || schedule.frequency === 'weekly') {
    if (schedule.nextDate) {
      let d = new Date(schedule.nextDate);
      if (d < startDate) {
         // fast forward
         const daysInterval = schedule.frequency === 'bi-weekly' ? 14 : 7;
         while (d < startDate) d = addDays(d, daysInterval);
      }
      while (d <= endDate) {
        occurrences.push({ date: d, amount: item.amount });
        d = addDays(d, schedule.frequency === 'bi-weekly' ? 14 : 7);
      }
    }
  }

  return occurrences;
};

export default function PlanningScheduleDashboard({ 
  plannedPayments, 
  recurringExpenses,
  debts,
  goals,
  incomes,
  transactions,
  paymentFiles = [],
  dismissedOccurrences = [],
  householdId,
  commitmentPaidMap = new Map(),
  onAddSchedule, 
  onEditPayment, 
  onMarkAsPaid 
}: PlanningScheduleDashboardProps) {
  
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set([format(new Date(), 'yyyy-MM')]));
  const [expandedCommitments, setExpandedCommitments] = useState<Set<string>>(new Set());
  const [isMissedExpanded, setIsMissedExpanded] = useState(false);
  const [uploadingState, setUploadingState] = useState<Record<string, boolean>>({});
  const [completingState, setCompletingState] = useState<Record<string, boolean>>({});
  const [isSuggestedExpanded, setIsSuggestedExpanded] = useState(true);
  // Session-only: a match stays hidden once approved or dismissed for this
  // visit, but isn't persisted anywhere -- it's recomputed fresh from
  // transactions/plannedPayments/virtualPayments every render, so an
  // approved one naturally drops out on its own once tx.linkedId is set
  // (this set only needs to cover the "dismiss without approving" case).
  const [dismissedMatchIds, setDismissedMatchIds] = useState<Set<string>>(new Set());
  const [adjustedChoice, setAdjustedChoice] = useState<Record<string, string>>({});
  const [approvingMatchId, setApprovingMatchId] = useState<string | null>(null);
  const [expandedMatchId, setExpandedMatchId] = useState<string | null>(null);

  /** Manually complete a custom planned payment at its actual paidAmount */
  const handleManualComplete = async (payment: VirtualPayment) => {
    if (!payment.id || payment.isVirtual) return;
    const pp = plannedPayments.find(p => p.id === payment.id);
    if (!pp) return;
    setCompletingState(prev => ({ ...prev, [payment.id]: true }));
    try {
      await updateDoc(doc(db, 'planned_payments', payment.id), {
        status: 'paid',
        manuallyCompleted: true,
        // paidAmount stays at whatever was already there — actual spend is what counts
      });
    } catch (err) {
      console.error('Error completing commitment:', err);
    } finally {
      setCompletingState(prev => ({ ...prev, [payment.id]: false }));
    }
  };

  /** Revert a manually completed custom payment back to pending */
  const handleRevertComplete = async (payment: VirtualPayment) => {
    if (!payment.id || payment.isVirtual) return;
    setCompletingState(prev => ({ ...prev, [payment.id]: true }));
    try {
      const pp = plannedPayments.find(p => p.id === payment.id);
      const paidAmount = commitmentPaidMap.get(payment.id) || 0;
      const isStillFullyPaid = paidAmount >= payment.amount;
      await updateDoc(doc(db, 'planned_payments', payment.id), {
        status: isStillFullyPaid ? 'paid' : 'pending',
        manuallyCompleted: false,
      });
    } catch (err) {
      console.error('Error reverting commitment:', err);
    } finally {
      setCompletingState(prev => ({ ...prev, [payment.id]: false }));
    }
  };

  /** Manually close a virtual (debt/recurring-expense) occurrence even
   *  though its linked transaction total came in under the projected
   *  amount — e.g. a real, lower payment for one billing cycle. Doesn't
   *  touch any transaction, bank balance, or debt balance; it only stops
   *  this specific occurrence from showing as still owed. */
  const handleDismissVirtualOccurrence = async (vp: VirtualPayment) => {
    if (!vp.isVirtual || !vp.sourceId) return;
    const dateKey = format(vp.date, 'yyyy-MM-dd');
    const docId = dismissedOccurrenceDocId(vp.sourceId, dateKey);
    setCompletingState(prev => ({ ...prev, [vp.id]: true }));
    try {
      await setDoc(doc(db, 'dismissed_occurrences', docId), {
        householdId,
        sourceId: vp.sourceId,
        dateKey,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error dismissing occurrence:', err);
    } finally {
      setCompletingState(prev => ({ ...prev, [vp.id]: false }));
    }
  };

  /** Undo a manual dismissal — the occurrence goes back to reflecting
   *  whatever its real linked transaction total actually covers. */
  const handleRevertVirtualDismissal = async (vp: VirtualPayment) => {
    if (!vp.isVirtual || !vp.sourceId) return;
    const dateKey = format(vp.date, 'yyyy-MM-dd');
    const docId = dismissedOccurrenceDocId(vp.sourceId, dateKey);
    setCompletingState(prev => ({ ...prev, [vp.id]: true }));
    try {
      await deleteDoc(doc(db, 'dismissed_occurrences', docId));
    } catch (err) {
      console.error('Error reverting dismissed occurrence:', err);
    } finally {
      setCompletingState(prev => ({ ...prev, [vp.id]: false }));
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, sourceId: string, monthKey: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const uploadKey = `${sourceId}_${monthKey}`;
    setUploadingState(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const fileRef = ref(storage, `households/${householdId}/receipts/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const fileUrl = await getDownloadURL(fileRef);

      await addDoc(collection(db, 'payment_files'), {
        householdId,
        sourceId,
        monthKey,
        fileUrl,
        fileName: file.name,
        createdAt: new Date()
      });
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Failed to upload file.');
    } finally {
      setUploadingState(prev => ({ ...prev, [uploadKey]: false }));
      // reset input
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId: string, fileUrl: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      await deleteDoc(doc(db, 'payment_files', fileId));
      // Optionally delete from storage, but we need the ref. We can just delete the doc for now.
    } catch (err) {
      console.error('Error deleting file doc:', err);
    }
  };

  const toggleMonth = (monthKey: string) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(monthKey)) next.delete(monthKey);
      else next.add(monthKey);
      return next;
    });
  };

  const toggleCommitment = (commitKey: string) => {
    setExpandedCommitments(prev => {
      const next = new Set(prev);
      if (next.has(commitKey)) next.delete(commitKey);
      else next.add(commitKey);
      return next;
    });
  };

  const virtualPayments = useMemo(() => {
    const today = new Date();
    // Start projecting from the 1st of the current month (not just today)
    // so a mid-month view still shows everything that already happened
    // this month -- income received, custom commitments due, recurring
    // expenses/debts already generated -- instead of only what's still
    // ahead. Older months stay excluded by this same window; the main
    // grouped view below applies its own current-month-start cutoff to
    // keep past *months* off the schedule as before.
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const endDate = endOfMonth(addMonths(today, 11)); // 12 months out
    // Debts/expenses also generate a look-back window (back to a fixed
    // date, not a rolling N months — see MISSED_PAYMENTS_TRACKING_START)
    // so a missed payment from the recent past can be surfaced (see
    // missedPayments below) instead of just disappearing once its month
    // rolls off this forward-looking view. Income is deliberately
    // excluded — missed income isn't a "payment" to cross off — and
    // custom plannedPayments already carry their own real historical
    // dates, no generation needed.
    const lookBackStart = MISSED_PAYMENTS_TRACKING_START;

    let vps: VirtualPayment[] = [];

    // 1. Custom Planned Payments
    plannedPayments.forEach(p => {
      if (p.date >= startDate && p.date <= endDate) {
        vps.push({
          id: p.id!,
          commitmentName: p.commitmentName,
          description: p.description,
          date: p.date,
          amount: p.amount,
          status: p.status,
          type: 'custom',
          sourceId: p.id,
          isVirtual: false
        });
      }
    });

    // Generate for Incomes
    incomes.forEach(inc => {
      const occs = generateOccurrences(inc, 'income', startDate, endDate);
      occs.forEach((occ, i) => {
        vps.push({
          id: `virt_inc_${inc.id}_${i}`,
          commitmentName: inc.source,
          description: 'Expected Income',
          date: occ.date,
          amount: occ.amount,
          status: 'pending',
          type: 'income',
          sourceId: inc.id,
          isVirtual: true
        });
      });
    });

    // Generate for Expenses
    recurringExpenses.forEach(exp => {
      const occs = generateOccurrences(exp, 'expense', lookBackStart, endDate);
      occs.forEach((occ, i) => {
        vps.push({
          id: `virt_exp_${exp.id}_${i}`,
          commitmentName: exp.name || exp.category,
          description: exp.notes || 'Fixed Expense',
          date: occ.date,
          amount: occ.amount,
          status: 'pending',
          type: 'expense',
          sourceId: exp.id,
          isVirtual: true
        });
      });
    });

    // Generate for Debts
    debts.forEach(debt => {
      const occs = generateOccurrences({ ...debt, amount: debt.monthlyMinimum }, 'debt', lookBackStart, endDate);
      occs.forEach((occ, i) => {
        vps.push({
          id: `virt_debt_${debt.id}_${i}`,
          commitmentName: debt.name,
          description: 'Debt Minimum Payment',
          date: occ.date,
          amount: occ.amount,
          status: 'pending',
          type: 'debt',
          sourceId: debt.id,
          isVirtual: true
        });
      });
    });

    // ---------------------------------------------------------------
    // Match transactions to virtual occurrences using nearest-date
    // allocation (pure absolute distance, whichever direction). This
    // still applies a grocery run on Aug 29 to the Sep 1 commitment (3
    // days away beats any prior occurrence a month back), and now also
    // correctly applies a Sep 3 catch-up payment to a missed Sep 1 bill
    // (2 days back) rather than skipping ahead to Oct 1 (28 days out) --
    // which is what a version of this that always preferred any future
    // occurrence over any past one used to do, silently misattributing
    // every late payment to the *next* bill instead of the one it was
    // actually paying off. Only matters now that occurrences include a
    // look-back window (see lookBackStart above) for missed-payment
    // detection; before that, past occurrences never existed here at all.
    // ---------------------------------------------------------------

    // Sort virtual occurrences chronologically first
    vps.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Index: sourceId -> list of {id, date} for all virtual occurrences
    const virtualsBySource = new Map<string, { id: string; date: Date }[]>();
    vps.forEach(vp => {
      if (vp.isVirtual && vp.sourceId) {
        if (!virtualsBySource.has(vp.sourceId)) {
          virtualsBySource.set(vp.sourceId, []);
        }
        virtualsBySource.get(vp.sourceId)!.push({ id: vp.id, date: vp.date });
      }
    });

    // Allocate each transaction to the correct virtual occurrence.
    // Priority:
    //   1. If tx.occurrenceDate is set (user explicitly chose a period), pin
    //      to the occurrence whose date matches yyyy-MM-dd exactly.
    //   2. Otherwise, use nearest-date heuristic (prefer upcoming).
    const txByVirtualId: Record<string, number> = {};
    transactions.forEach(tx => {
      if (!tx.linkedId || !tx.date) return;
      const occs = virtualsBySource.get(tx.linkedId);
      if (!occs || occs.length === 0) return;

      let target: { id: string; date: Date } | undefined;

      if (tx.occurrenceDate) {
        // --- Pinned mode: match by exact date string ---
        target = occs.find(o => {
          const d = o.date;
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          return key === tx.occurrenceDate;
        });
        // If no exact match (schedule changed), fall through to nearest-date
      }

      if (!target) {
        // --- Auto mode: nearest occurrence by absolute distance ---
        const txTime = (tx.date as Date).getTime();
        target = occs.reduce((a, b) =>
          Math.abs(a.date.getTime() - txTime) <= Math.abs(b.date.getTime() - txTime) ? a : b
        );
      }

      if (target) {
        txByVirtualId[target.id] = (txByVirtualId[target.id] || 0) + tx.amount;
      }
    });


    // A real payment linked to this occurrence but manually accepted as
    // closing it even though it came in under the projected amount (e.g.
    // a credit card's real minimum was lower than usual for one billing
    // cycle) — keyed by source + the exact occurrence date, since virtual
    // occurrences have no Firestore document of their own to flag directly.
    const dismissedSet = new Set(
      dismissedOccurrences.map(d => dismissedOccurrenceDocId(d.sourceId, d.dateKey))
    );

    // Update vp.status based on allocated amounts (or a manual dismissal)
    vps.forEach(vp => {
      if (vp.isVirtual && vp.sourceId) {
        const paid = txByVirtualId[vp.id] || 0;
        if (paid >= vp.amount * 0.9) { // 10% leeway for rounding
          vp.status = 'paid';
        } else if (dismissedSet.has(dismissedOccurrenceDocId(vp.sourceId, format(vp.date, 'yyyy-MM-dd')))) {
          vp.status = 'paid';
          vp.manuallyCompleted = true;
        }
      }
    });

    // Attach txByVirtualId so the renderer can access paid amounts per occurrence
    (vps as any).__txByVirtualId = txByVirtualId;

    return vps;
  }, [plannedPayments, recurringExpenses, debts, goals, incomes, transactions, dismissedOccurrences]);

  // Past-due commitments — the schedule below only ever projects from
  // today forward (by design, so old months don't clutter it), which
  // means a payment due last month that never got recorded would just
  // silently disappear instead of showing up as overdue. Covers both
  // custom one-time/installment plans (their own real dates, straight
  // from Firestore) and recurring debts/expenses (their virtual
  // occurrences within the 3-month look-back window generated above,
  // already run through the same transaction-matching pass as the main
  // view). Income is excluded — missed income isn't a "payment" to cross
  // off.
  const missedPayments = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const txByVirtualId: Record<string, number> = (virtualPayments as any).__txByVirtualId || {};

    const customMissed: MissedItem[] = plannedPayments
      .filter(p => p.date < todayStart && p.status !== 'paid' && !p.manuallyCompleted)
      .map(p => ({
        key: p.id!,
        commitmentName: p.commitmentName,
        description: p.description,
        date: p.date,
        amount: p.amount,
        // commitmentPaidMap is recomputed fresh from real transactions on
        // every render (the source of truth) — the stored paidAmount
        // field on the document is a denormalized cache that can go
        // stale (e.g. relinking a transaction away from this row only
        // clears its status/linkedTransactionId, not this field), so
        // it's deliberately not used as a fallback here.
        alreadyPaid: commitmentPaidMap.get(p.id!) || 0,
        isVirtual: false,
        plannedPaymentId: p.id,
      }));

    const recurringMissed: MissedItem[] = virtualPayments
      .filter(vp => vp.isVirtual && vp.type !== 'income' && vp.date < todayStart && vp.status !== 'paid')
      .map(vp => ({
        key: vp.id,
        commitmentName: vp.commitmentName,
        description: vp.description,
        date: vp.date,
        amount: vp.amount,
        alreadyPaid: txByVirtualId[vp.id] || 0,
        isVirtual: true,
        virtualPayment: vp,
      }));

    return [...customMissed, ...recurringMissed].sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [plannedPayments, virtualPayments, commitmentPaidMap]);

  // How close a transaction's date has to be to a commitment's date to be
  // worth suggesting at all -- wide enough to catch a bill that posted a
  // few days late, narrow enough not to nag with implausible pairings.
  const SUGGEST_MATCH_WINDOW_DAYS = 10;

  // Suggests -- never auto-links -- an unlinked expense transaction against
  // a still-open commitment it might be paying off. Debt virtual occurrences
  // used to be excluded from the candidate pool entirely, so a genuine
  // mortgage payment could never find a mortgage option in its match
  // dropdown at all -- fixed by including debts as a third candidate kind
  // here, same date-proximity matching as recurring expenses.
  //
  // Whether the paying transaction should still be *approved* against that
  // debt depends on whether the debt's own account is separately
  // Plaid-linked. When only the paying bank account is linked (the
  // debt/mortgage account isn't), the payment imports as a plain, unlinked
  // 'expense' with no other way to ever reach the debt, so approving is
  // exactly right: it reclassifies the transaction as a real debt_payment
  // and decrements the debt's tracked balance (handleApproveMatch's 'debt'
  // branch), since that decrement never happened at import time otherwise.
  // But when the debt's own account IS also Plaid-linked, its own feed can
  // independently sync a correctly-classified debt_payment for the same
  // real-world payment (deriveTransactionFields in
  // statementReconciliation.ts, accountType:'debt' + direction:'credit')
  // that already decremented the balance -- duplicateClassification.ts
  // can't catch that pairing across accounts, so both sides still land as
  // separate, seemingly-unlinked transactions and this one still shows up
  // as a suggested match. handleApproveMatch's 'debt' branch guards against
  // double-decrementing in that case; see the comment there.
  // One-off custom commitments still have no automatic step either;
  // matching by amount alone isn't reliable for either (a recurring
  // expense's tracked amount is often an estimate, not what actually gets
  // charged), so this matches purely by date proximity and leaves amount as
  // something to glance at, not a filter.
  const suggestedMatches = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 90);

    const unlinkedExpenses = transactions.filter(tx => tx.type === 'expense' && !tx.linkedId && tx.date >= cutoff);
    if (unlinkedExpenses.length === 0) return [];

    const candidates: MatchCandidate[] = [];
    plannedPayments.forEach(p => {
      if (p.status !== 'pending' || !p.id) return;
      candidates.push({
        key: `custom_${p.id}`,
        label: `${p.commitmentName} — ${format(p.date, 'MMM d')} · $${p.amount.toFixed(2)}`,
        date: p.date,
        amount: p.amount,
        kind: 'custom',
        plannedPaymentId: p.id,
      });
    });
    virtualPayments.forEach(vp => {
      if (!vp.isVirtual || vp.status !== 'pending' || !vp.sourceId) return;
      if (vp.type !== 'expense' && vp.type !== 'debt') return;
      candidates.push({
        key: `${vp.type}_${vp.id}`,
        label: `${vp.commitmentName} — ${format(vp.date, 'MMM d')} · $${vp.amount.toFixed(2)}`,
        date: vp.date,
        amount: vp.amount,
        kind: vp.type === 'debt' ? 'debt' : 'virtual',
        virtualSourceId: vp.sourceId,
      });
    });
    if (candidates.length === 0) return [];

    const results: SuggestedMatch[] = [];
    unlinkedExpenses.forEach(tx => {
      if (!tx.id) return;
      const txTime = (tx.date as Date).getTime();
      const ranked = candidates
        .map(c => ({ c, distanceDays: Math.abs(c.date.getTime() - txTime) / 86400000 }))
        .filter(x => x.distanceDays <= SUGGEST_MATCH_WINDOW_DAYS)
        .sort((a, b) => a.distanceDays - b.distanceDays)
        .slice(0, 3)
        .map(x => x.c);
      if (ranked.length === 0) return;
      results.push({
        transactionId: tx.id,
        transactionDescription: tx.description,
        transactionDate: tx.date,
        transactionAmount: tx.amount,
        candidates: ranked,
      });
    });

    return results
      .filter(m => !dismissedMatchIds.has(m.transactionId))
      .sort((a, b) => a.transactionDate.getTime() - b.transactionDate.getTime());
  }, [transactions, plannedPayments, virtualPayments, dismissedMatchIds]);

  const handleApproveMatch = async (match: SuggestedMatch) => {
    const chosenKey = adjustedChoice[match.transactionId] || match.candidates[0].key;
    const chosen = match.candidates.find(c => c.key === chosenKey);
    if (!chosen) return;

    setApprovingMatchId(match.transactionId);
    try {
      if (chosen.kind === 'virtual') {
        // Mirrors TransactionModal's manual "link to a recurring expense"
        // dropdown exactly -- linkedId is just that expense's own doc id;
        // the nearest-date allocation pass building virtualPayments above
        // picks it up from there automatically, nothing else to write.
        await updateDoc(doc(db, 'transactions', match.transactionId), { linkedId: chosen.virtualSourceId });
      } else if (chosen.kind === 'debt') {
        // This transaction is currently a plain 'expense'. Usually that's
        // because the debt has no Plaid-linked account of its own, so its
        // payment only ever appears on the paying bank account's feed and
        // never gets classified as a debt_payment automatically. BUT: when
        // the debt itself *is* Plaid-linked (a mortgage/LOC/card synced
        // directly), the debt's own feed can independently produce a
        // correctly-classified debt_payment (deriveTransactionFields in
        // functions/src/statementReconciliation.ts, accountType:'debt' +
        // direction:'credit') that already decremented currentAmount --
        // duplicateClassification.ts can't catch that pairing across
        // accounts (a debt-side debt_payment is written with
        // bankAccountId: null, so the bank-side row never matches it), so
        // both sides land as independent, unlinked-looking transactions.
        // Guard against reclassifying+decrementing a transaction whose
        // real-world payment the debt's own feed already recorded, which
        // would silently double-subtract from the balance.
        if (!chosen.virtualSourceId) return;
        const DUPLICATE_WINDOW_DAYS = 7;
        const alreadyRecordedOnDebtSide = transactions.some(tx =>
          tx.type === 'debt_payment' &&
          tx.linkedId === chosen.virtualSourceId &&
          tx.id !== match.transactionId &&
          Math.abs(tx.date.getTime() - match.transactionDate.getTime()) / (1000 * 60 * 60 * 24) <= DUPLICATE_WINDOW_DAYS
        );
        if (alreadyRecordedOnDebtSide) {
          alert("This debt already has its own payment recorded around this date (likely synced directly from the debt's own Plaid-linked account). Linking this transaction too would subtract it from your balance twice. Dismiss this suggested match instead -- no action needed on this transaction.");
          return;
        }
        await updateDoc(doc(db, 'transactions', match.transactionId), {
          type: 'debt_payment',
          linkedId: chosen.virtualSourceId,
        });
        await updateDoc(doc(db, 'debts', chosen.virtualSourceId), {
          currentAmount: increment(-match.transactionAmount),
        });
      } else if (chosen.plannedPaymentId) {
        // Mirrors TransactionModal's manual "link to a custom commitment"
        // path exactly (see its own linkedId.startsWith('planned_') branch).
        const pp = plannedPayments.find(p => p.id === chosen.plannedPaymentId);
        if (!pp) return;
        const newPaidAmount = (pp.paidAmount || 0) + match.transactionAmount;
        const isFullyPaid = newPaidAmount >= pp.amount;
        await updateDoc(doc(db, 'transactions', match.transactionId), { linkedId: `planned_${chosen.plannedPaymentId}` });
        await updateDoc(doc(db, 'planned_payments', chosen.plannedPaymentId), {
          paidAmount: newPaidAmount,
          status: isFullyPaid ? 'paid' : 'pending',
          linkedTransactionId: match.transactionId,
        });
      }
    } catch (e) {
      console.error('Failed to approve suggested match', e);
      alert('Failed to link this transaction. Please try again.');
    } finally {
      setApprovingMatchId(null);
    }
  };

  const handleDismissMatch = (transactionId: string) => {
    setDismissedMatchIds(prev => new Set(prev).add(transactionId));
  };

  const groupedPayments = useMemo(() => {
    // month -> category (type) -> commitmentName -> payments
    const groups: Record<string, Record<string, Record<string, VirtualPayment[]>>> = {};
    // txByVirtualId: keyed by virtual payment unique ID (not monthKey+sourceId)
    const txByVirtualId: Record<string, number> = (virtualPayments as any).__txByVirtualId || {};
    const today = new Date();
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    virtualPayments.forEach(payment => {
      // The look-back window added above (for missed-payment detection)
      // means virtualPayments can now contain occurrences from past
      // months — keep those off this view (missedPayments is where they
      // surface instead). Anything within the current month is kept,
      // including days already passed, so the breakdown always shows the
      // whole month-to-date, not just what's still ahead of today.
      if (payment.date < currentMonthStart) return;
      const monthKey = format(payment.date, 'yyyy-MM');
      if (!groups[monthKey]) {
        groups[monthKey] = {};
      }
      if (!groups[monthKey][payment.type]) {
        groups[monthKey][payment.type] = {};
      }
      if (!groups[monthKey][payment.type][payment.commitmentName]) {
        groups[monthKey][payment.type][payment.commitmentName] = [];
      }
      groups[monthKey][payment.type][payment.commitmentName].push(payment);
    });

    return { groups, txByVirtualId };
  }, [virtualPayments]);

  const { groups: groupedPaymentsData, txByVirtualId: txMap } = groupedPayments;

  const chartData = useMemo(() => {
    return Object.entries(groupedPaymentsData).map(([monthKey, categories]) => {
      const monthLabel = format(parseISO(monthKey + '-01'), 'MMM yyyy');
      let outTotal = 0;
      let outPaid = 0;
      let incTotal = 0;

      Object.values(categories).forEach(commitments => {
        (Object.values(commitments) as VirtualPayment[][]).forEach(payments => {
          payments.forEach(p => {
            if (p.type === 'income') {
              incTotal += p.amount;
            } else {
              outTotal += p.amount;
              if (p.status === 'paid') outPaid += p.amount;
            }
          });
        });
      });
      return {
        monthKey,
        name: monthLabel,
        Total: outTotal,
        Paid: outPaid,
        Pending: outTotal - outPaid,
        Income: incTotal
      };
    });
  }, [groupedPaymentsData]);

  const handleDelete = async (id?: string, isVirtual?: boolean) => {
    if (!id) return;
    if (isVirtual) {
       alert("This is a recurring commitment. To delete it, please delete the original item from its respective tab.");
       return;
    }
    if (window.confirm("Are you sure you want to delete this planned payment?")) {
      await deleteDoc(doc(db, 'planned_payments', id));
    }
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case 'income': return <Banknote className="w-4 h-4 text-green-600" />;
      case 'expense': return <Wallet className="w-4 h-4 text-rose-600" />;
      case 'debt': return <CreditCard className="w-4 h-4 text-indigo-600" />;
      case 'goal': return <Target className="w-4 h-4 text-emerald-600" />;
      default: return <Calendar className="w-4 h-4 text-blue-600" />;
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-6xl mx-auto w-full">
      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 rounded-xl p-6 md:p-8 text-white shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 mb-2">
            <Calendar className="w-6 h-6 text-blue-400" />
            Unified Planning Schedule
          </h2>
          <p className="text-blue-100 max-w-xl text-sm leading-relaxed">
            Your master control center for all commitments. Visualize one-time payments, recurring expenses, debts, and expected income month-by-month.
          </p>
        </div>
        <button
          onClick={onAddSchedule}
          className="flex-shrink-0 bg-white text-blue-900 px-6 py-3 rounded-lg font-bold shadow-md hover:bg-blue-50 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Commitment
        </button>
      </div>

      {suggestedMatches.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl overflow-hidden shadow-sm">
          <div
            onClick={() => setIsSuggestedExpanded(prev => !prev)}
            className="px-4 sm:px-6 py-4 border-b border-blue-200 bg-blue-100/60 flex items-center gap-2 cursor-pointer hover:bg-blue-100 transition-colors"
          >
            <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <h3 className="font-bold text-blue-900 text-base sm:text-lg">Suggested Matches</h3>
            <span className="text-xs bg-blue-200 text-blue-800 font-bold px-2 py-0.5 rounded-full">{suggestedMatches.length}</span>
            <span className="text-xs text-blue-700 ml-auto hidden sm:inline">Synced transactions that might pay off an open commitment — nothing links until you approve it</span>
            <ChevronDown className={`w-5 h-5 text-blue-600 flex-shrink-0 transition-transform ${isSuggestedExpanded ? 'rotate-180' : ''}`} />
          </div>
          {isSuggestedExpanded && (
            <div className="divide-y divide-blue-100">
              {suggestedMatches.map(match => {
                const chosenKey = adjustedChoice[match.transactionId] || match.candidates[0].key;
                const isApproving = approvingMatchId === match.transactionId;
                const isExpanded = expandedMatchId === match.transactionId;
                const fullTx = transactions.find(t => t.id === match.transactionId);
                return (
                  <div key={match.transactionId}>
                    <div className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{match.transactionDescription || 'Transaction'}</p>
                        <p className="text-xs text-slate-500">{format(match.transactionDate, 'MMM d, yyyy')} · ${match.transactionAmount.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 text-sm shrink-0">→</div>
                      <div className="flex-1 min-w-0">
                        <select
                          value={chosenKey}
                          onChange={(e) => setAdjustedChoice(prev => ({ ...prev, [match.transactionId]: e.target.value }))}
                          className="w-full text-sm font-medium border border-blue-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {match.candidates.map(c => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => setExpandedMatchId(isExpanded ? null : match.transactionId)}
                          className="text-xs font-semibold text-blue-600 px-2 py-1.5 rounded-md hover:bg-blue-100"
                        >
                          {isExpanded ? 'Hide' : 'View'}
                        </button>
                        <button
                          onClick={() => handleApproveMatch(match)}
                          disabled={isApproving}
                          className="text-xs font-semibold bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {isApproving ? 'Linking…' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleDismissMatch(match.transactionId)}
                          className="text-xs font-semibold text-slate-500 px-3 py-1.5 rounded-md hover:bg-slate-200"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-3 sm:px-4 pb-3 sm:pb-4 -mt-1">
                        <div className="text-xs text-slate-600 mb-2 font-medium">Transaction details:</div>
                        {fullTx ? (
                          <div className="flex items-center gap-4 bg-white p-2 border border-blue-200 rounded shadow-sm">
                            <span className="text-slate-500 text-xs w-20 shrink-0">{format(fullTx.date, 'MMM d, yyyy')}</span>
                            <span className="text-slate-800 text-xs flex-1 truncate" title={fullTx.description}>{fullTx.description || '—'}</span>
                            <span className="text-slate-500 text-xs w-32 truncate">{fullTx.category || '—'}</span>
                            <span className={`text-xs font-medium w-24 text-right shrink-0 ${fullTx.type === 'income' ? 'text-emerald-600' : 'text-slate-700'}`}>
                              {fullTx.type === 'income' ? '+' : '−'}${fullTx.amount.toFixed(2)}
                            </span>
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400">Transaction details unavailable.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {missedPayments.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden shadow-sm">
          <div
            onClick={() => setIsMissedExpanded(prev => !prev)}
            className="px-4 sm:px-6 py-4 border-b border-amber-200 bg-amber-100/60 flex items-center gap-2 cursor-pointer hover:bg-amber-100 transition-colors"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <h3 className="font-bold text-amber-900 text-base sm:text-lg">Missed Payments</h3>
            <span className="text-xs bg-amber-200 text-amber-800 font-bold px-2 py-0.5 rounded-full">{missedPayments.length}</span>
            <span className="text-xs text-amber-700 ml-auto hidden sm:inline">Past due — didn't get recorded before their month rolled off the schedule below</span>
            <ChevronDown className={`w-5 h-5 text-amber-600 flex-shrink-0 transition-transform ${isMissedExpanded ? 'rotate-180' : ''}`} />
          </div>
          {isMissedExpanded && (
            <div className="divide-y divide-amber-100">
              {missedPayments.map(item => {
                const remaining = Math.max(0, item.amount - item.alreadyPaid);
                const daysLate = differenceInCalendarDays(new Date(), item.date);
                const isCompleting = item.isVirtual
                  ? (item.virtualPayment ? completingState[item.virtualPayment.id] : false)
                  : (item.plannedPaymentId ? completingState[item.plannedPaymentId] : false);
                return (
                  <div key={item.key} className="p-3 sm:p-4 flex items-center justify-between gap-3 hover:bg-amber-50/60 transition-colors">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <button
                        onClick={() => {
                          if (item.isVirtual) {
                            if (item.virtualPayment) handleDismissVirtualOccurrence(item.virtualPayment);
                          } else if (item.plannedPaymentId) {
                            handleManualComplete({ id: item.plannedPaymentId, isVirtual: false } as VirtualPayment);
                          }
                        }}
                        disabled={isCompleting}
                        className="mt-0.5 flex-shrink-0 text-amber-400 hover:text-blue-500 cursor-pointer transition-colors"
                        title={item.isVirtual ? "Accept what's already recorded and close this out (e.g. a lower real payment for this cycle)" : 'Mark as complete'}
                      >
                        {isCompleting ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : <Circle className="w-5 h-5" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-sm text-slate-800">{item.commitmentName}</p>
                          {item.isVirtual && <span className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1 rounded border border-slate-200">Auto-Projected</span>}
                        </div>
                        <p className="text-xs text-slate-600 break-words">{item.description}</p>
                        <div className="text-[10px] sm:text-xs text-amber-700 mt-1 flex items-center gap-1 font-semibold">
                          <Clock className="w-3 h-3" /> Due {format(item.date, 'MMM d, yyyy')} · {daysLate} day{daysLate === 1 ? '' : 's'} late
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm sm:text-base font-bold text-slate-900">${remaining.toFixed(2)}</p>
                      {item.alreadyPaid > 0 && (
                        <p className="text-[10px] text-blue-600 font-semibold">${item.alreadyPaid.toFixed(2)} already recorded</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Graph Section */}
        <div className="lg:col-span-3 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-6">Monthly Forecast</h3>
          {chartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm italic">
              No data to forecast.
            </div>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8', fontWeight: 'bold' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8', fontWeight: 'bold' }} tickFormatter={(val) => `$${val}`} />
                  <Tooltip 
                    cursor={{ fill: '#f8fafc' }}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, undefined]}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                  <Bar dataKey="Income" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="Pending" stackId="a" fill="#ef4444" radius={[0, 0, 0, 0]} maxBarSize={40} name="Pending Outflows" />
                  <Bar dataKey="Paid" stackId="a" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={40} name="Paid Outflows" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Month by Month List */}
        <div className="lg:col-span-3 space-y-6">
          {Object.keys(groupedPaymentsData).length === 0 ? (
            <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No commitments scheduled.</p>
            </div>
          ) : (
            Object.keys(groupedPaymentsData).sort().map(monthKey => {
              const categories = groupedPaymentsData[monthKey];
              const monthDate = parseISO(monthKey + '-01');
              const isCurrentMonth = isSameMonth(monthDate, new Date());
              const isMonthExpanded = expandedMonths.has(monthKey);

              let outDue = 0;
              let outPaid = 0;
              let incTotal = 0;

              Object.values(categories).forEach(commitments => {
                (Object.values(commitments) as VirtualPayment[][]).forEach(payments => {
                  payments.forEach(p => {
                    if (p.type === 'income') {
                      incTotal += p.amount;
                    } else {
                      outDue += p.amount;
                      if (p.status === 'paid') outPaid += p.amount;
                    }
                  });
                });
              });
              const remainingOut = outDue - outPaid;

              return (
                <div key={monthKey} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${isCurrentMonth ? 'border-blue-300 ring-1 ring-blue-100' : 'border-slate-200'}`}>
                  <div 
                    onClick={() => toggleMonth(monthKey)}
                    className={`px-4 sm:px-6 py-4 border-b flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors ${isCurrentMonth ? 'bg-blue-50/50' : 'bg-slate-50'}`}
                  >
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2 flex-wrap">
                        {format(monthDate, 'MMMM yyyy')}
                        {isCurrentMonth && <span className="text-[10px] bg-blue-100 text-blue-700 uppercase tracking-wider px-2 py-0.5 rounded font-bold">Current Month</span>}
                      </h3>
                    </div>
                    <div className="text-right flex items-center gap-4">
                      <div className="hidden sm:flex gap-6 text-right">
                        <div>
                          <p className="text-xs text-slate-400 uppercase font-bold tracking-wider">Income</p>
                          <p className="text-sm font-bold text-emerald-600">+${incTotal.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 uppercase font-bold tracking-wider">Outflows</p>
                          <p className="text-sm font-bold text-slate-900">-${outDue.toFixed(2)}</p>
                          <p className="text-[10px] font-semibold text-blue-600">${remainingOut.toFixed(2)} Left</p>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isMonthExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  
                  {isMonthExpanded && (
                    <div className="p-3 sm:p-4 space-y-5 bg-white">
                      {CATEGORY_ORDER.filter(cat => categories[cat]).map(cat => {
                        const commitments = categories[cat];
                        const isIncomeCat = cat === 'income';
                        let catDue = 0;
                        let catPaid = 0;
                        (Object.values(commitments) as VirtualPayment[][]).forEach(payments => {
                          payments.forEach(p => {
                            catDue += p.amount;
                            if (p.status === 'paid') catPaid += p.amount;
                          });
                        });

                        return (
                          <div key={cat}>
                            <div className="flex items-center justify-between mb-2 px-1">
                              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                                {getIconForType(cat)} {CATEGORY_LABELS[cat]}
                              </h4>
                              <span className={`text-xs font-bold ${isIncomeCat ? 'text-emerald-600' : 'text-slate-500'}`}>
                                {isIncomeCat ? '+' : '-'}${catDue.toFixed(2)}
                                {!isIncomeCat && catPaid > 0 && catPaid < catDue && (
                                  <span className="text-amber-600"> · ${(catDue - catPaid).toFixed(2)} left</span>
                                )}
                              </span>
                            </div>
                            <div className="space-y-3">
                      {Object.entries(commitments).map(([commitName, paymentsArray]) => {
                        const payments = paymentsArray as VirtualPayment[];
                        const commitKey = `${monthKey}-${cat}-${commitName}`;
                        const isCommitExpanded = expandedCommitments.has(commitKey);
                        const type = payments[0].type;
                        
                        const commitTotalDue = payments.reduce((sum, p) => sum + p.amount, 0);
                        const allPaid = type === 'income' ? false : payments.every(p => p.status === 'paid');

                        // Compute fractional paid amount for this commitment group
                        let commitPaidAmount = 0;
                        if (type === 'custom') {
                          // Custom: sum commitmentPaidMap across every row in
                          // this group — a commitment like a payment plan
                          // has multiple rows (registration fee, several
                          // installments) sharing one commitmentName. This
                          // used to look up only a single row via .find()
                          // (whichever one happened to match first) and use
                          // just *that* row's paid amount to represent the
                          // whole group, so a fully-paid registration fee
                          // showed as "the" progress while every other paid
                          // installment was silently ignored. commitmentPaidMap
                          // (not the row's own stored paidAmount, which can go
                          // stale — see the Missed Payments section above) is
                          // recomputed fresh from real transactions every
                          // render, so it's the reliable source here.
                          payments.forEach(p => {
                            commitPaidAmount += commitmentPaidMap.get(p.id) || 0;
                          });
                        } else {
                          // Virtual: use txMap keyed by virtual payment's unique ID
                          // (supports cross-month allocation — e.g. Aug 29 tx → Sep 1 commitment)
                          payments.forEach(p => {
                            commitPaidAmount += txMap[p.id] || 0;
                          });
                        }
                        const progressPct = Math.min(100, commitTotalDue > 0 ? (commitPaidAmount / commitTotalDue) * 100 : 0);
                        const isPartiallyPaid = commitPaidAmount > 0 && !allPaid;

                        return (
                          <div key={commitKey} className="border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                            <div 
                              onClick={() => toggleCommitment(commitKey)}
                              className={`px-3 sm:px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-slate-100 transition-colors ${allPaid ? 'bg-blue-50/30' : 'bg-slate-50'}`}
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <ChevronRight className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isCommitExpanded ? 'rotate-90' : ''}`} />
                                <div className="flex flex-col min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {getIconForType(type)}
                                    <span className={`font-bold text-sm sm:text-base ${allPaid ? 'text-blue-800 line-through opacity-60' : 'text-slate-800'}`}>{commitName}</span>
                                    {allPaid && <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 uppercase px-1.5 py-0.5 rounded font-bold tracking-wider">Paid</span>}
                                    {isPartiallyPaid && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 uppercase px-1.5 py-0.5 rounded font-bold tracking-wider">Partial</span>}
                                  </div>
                                  {/* Fractional progress bar */}
                                  {type !== 'income' && commitPaidAmount > 0 && (
                                    <div className="mt-1.5 flex items-center gap-2">
                                      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden max-w-[140px]">
                                        <div
                                          className={`h-full rounded-full transition-all ${allPaid ? 'bg-blue-500' : 'bg-amber-400'}`}
                                          style={{ width: `${progressPct}%` }}
                                        />
                                      </div>
                                      <span className="text-[10px] text-slate-500 font-medium whitespace-nowrap">
                                        ${commitPaidAmount.toFixed(2)} / ${commitTotalDue.toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="text-right flex items-center gap-3 flex-shrink-0">
                                <div className="text-right">
                                  <p className={`text-sm font-bold ${type === 'income' ? 'text-emerald-600' : 'text-slate-900'}`}>{type === 'income' ? '+' : '-'}${commitTotalDue.toFixed(2)}</p>
                                </div>
                              </div>
                            </div>
                            
                            {isCommitExpanded && (
                              <div className="divide-y divide-slate-100 border-t border-slate-100 bg-white">
                                {payments.map(payment => {
                                  const sourceIdentifier = payment.sourceId || payment.id;
                                  const uploadKey = `${sourceIdentifier}_${monthKey}`;
                                  const existingFile = paymentFiles.find(f => f.sourceId === sourceIdentifier && f.monthKey === monthKey);
                                  const isUploading = uploadingState[uploadKey];
                                  const isCompleting = completingState[payment.id];

                                  // Determine paid amount for this specific payment row
                                  let rowPaidAmount = 0;
                                  if (!payment.isVirtual) {
                                    rowPaidAmount = commitmentPaidMap.get(payment.id) || 0;
                                  } else {
                                    // Keyed by virtual payment unique ID (cross-month safe)
                                    rowPaidAmount = txMap[payment.id] || 0;
                                  }
                                  const rowPct = Math.min(100, payment.amount > 0 ? (rowPaidAmount / payment.amount) * 100 : 0);
                                  const pp = !payment.isVirtual ? plannedPayments.find(p => p.id === payment.id) : null;
                                  const isManuallyCompleted = pp?.manuallyCompleted || false;

                                  return (
                                    <div key={payment.id} className={`p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors hover:bg-slate-50/50 ${payment.status === 'paid' ? 'opacity-60 bg-slate-50' : ''}`}>
                                      <div className="flex items-start gap-3 w-full sm:w-auto">
                                        {payment.type !== 'income' && (
                                          <button 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (isCompleting) return;
                                              if (payment.isVirtual) return; // virtual: read-only
                                              if (payment.status === 'paid' || isManuallyCompleted) {
                                                handleRevertComplete(payment);
                                              } else {
                                                handleManualComplete(payment);
                                              }
                                            }}
                                            disabled={isCompleting || payment.isVirtual}
                                            className={`mt-0.5 flex-shrink-0 transition-colors ${
                                              payment.isVirtual
                                                ? (payment.status === 'paid' ? 'text-blue-400 cursor-default' : 'text-slate-200 cursor-default')
                                                : (payment.status === 'paid' || isManuallyCompleted)
                                                  ? 'text-blue-500 hover:text-amber-500 cursor-pointer'
                                                  : 'text-slate-300 hover:text-blue-500 cursor-pointer'
                                            }`}
                                            title={
                                              payment.isVirtual
                                                ? (payment.status === 'paid' ? 'Auto-marked paid from linked transactions' : 'Link transactions in Overview to fulfill')
                                                : (payment.status === 'paid' || isManuallyCompleted)
                                                  ? 'Click to revert to pending'
                                                  : 'Mark as complete (use Overview transactions to partially fulfill)'
                                            }
                                          >
                                            {isCompleting
                                              ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                                              : (payment.status === 'paid' || isManuallyCompleted)
                                                ? <CheckCircle2 className="w-5 h-5" />
                                                : <Circle className="w-5 h-5" />
                                            }
                                          </button>
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs sm:text-sm text-slate-700 break-words line-clamp-2">{payment.description}</p>
                                          <div className="text-[10px] sm:text-xs text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {format(payment.date, 'MMM do')}</span>
                                            {payment.isVirtual && <span className="uppercase tracking-wider bg-slate-100 px-1 rounded border border-slate-200 text-[9px]">Auto-Projected</span>}
                                            {isManuallyCompleted && <span className="uppercase tracking-wider bg-amber-50 text-amber-600 px-1 rounded border border-amber-100 text-[9px]">Manually Closed</span>}
                                            
                                            {/* Fractional progress for this payment row */}
                                            {payment.type !== 'income' && rowPaidAmount > 0 && payment.status !== 'paid' && !isManuallyCompleted && (
                                              <span className="text-[10px] text-blue-500 font-semibold">
                                                ${rowPaidAmount.toFixed(2)} recorded
                                              </span>
                                            )}

                                            {/* File Upload UI */}
                                            {payment.type !== 'income' && (
                                              <div className="flex items-center gap-1 ml-2">
                                                {existingFile ? (
                                                  <div className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-100">
                                                    <Paperclip className="w-3 h-3" />
                                                    <a href={existingFile.fileUrl} target="_blank" rel="noreferrer" className="hover:underline max-w-[100px] truncate" title={existingFile.fileName}>
                                                      {existingFile.fileName}
                                                    </a>
                                                    <button onClick={() => handleDeleteFile(existingFile.id!, existingFile.fileUrl)} className="text-blue-400 hover:text-rose-500 ml-1">
                                                      <Trash2 className="w-3 h-3" />
                                                    </button>
                                                  </div>
                                                ) : isUploading ? (
                                                  <span className="flex items-center gap-1 text-slate-400 px-2 py-0.5 rounded-full border border-slate-200">
                                                    <Loader2 className="w-3 h-3 animate-spin" /> Uploading...
                                                  </span>
                                                ) : (
                                                  <label className="flex items-center gap-1 text-slate-400 hover:text-indigo-500 cursor-pointer px-2 py-0.5 rounded-full border border-transparent hover:border-indigo-100 hover:bg-indigo-50 transition-colors">
                                                    <Upload className="w-3 h-3" /> Upload Receipt
                                                    <input 
                                                      type="file" 
                                                      className="hidden" 
                                                      accept="image/*,.pdf"
                                                      onChange={(e) => handleFileUpload(e, sourceIdentifier, monthKey)}
                                                    />
                                                  </label>
                                                )}
                                              </div>
                                            )}
                                          </div>

                                          {/* Per-row progress bar */}
                                          {payment.type !== 'income' && rowPaidAmount > 0 && payment.status !== 'paid' && (
                                            <div className="mt-2 flex items-center gap-2">
                                              <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden max-w-[120px]">
                                                <div
                                                  className="h-full bg-blue-400 rounded-full transition-all"
                                                  style={{ width: `${rowPct}%` }}
                                                />
                                              </div>
                                              <span className="text-[10px] text-slate-400">
                                                {rowPct.toFixed(0)}% of ${payment.amount.toFixed(2)}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto pl-8 sm:pl-0">
                                        <span className={`text-sm sm:text-base font-bold ${payment.status === 'paid' ? 'text-slate-500 line-through' : (payment.type === 'income' ? 'text-emerald-600' : 'text-slate-900')}`}>
                                          ${payment.amount.toFixed(2)}
                                        </span>
                                        {!payment.isVirtual && (
                                          <div className="flex items-center gap-1 sm:gap-2">
                                            <button 
                                              onClick={() => onEditPayment(payment as unknown as PlannedPayment)}
                                              className="p-1.5 sm:p-2 text-slate-400 hover:text-blue-500 transition-colors rounded-md hover:bg-blue-50"
                                              title="Edit payment"
                                            >
                                              <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button 
                                              onClick={() => handleDelete(payment.id, payment.isVirtual)}
                                              className="p-1.5 sm:p-2 text-slate-400 hover:text-rose-500 transition-colors rounded-md hover:bg-rose-50"
                                              title="Delete payment"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
